import { useState } from "react";
import { useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { AlertCircle, Check, Loader2, UserPlus, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";

import { toast } from "@/utils/customToast";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { axiosApiInstance } from "@/libs/axios";
import { WORKSPACES_API_ENDPOINTS } from "@/constants/api";
import useUserStore from "@/features/auth/store/useUserStore";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore";

type RejoinStatus = "confirm" | "rejoining" | "success" | "error";

interface RejoinWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RejoinWorkspaceDialog = ({ isOpen, onClose }: RejoinWorkspaceDialogProps) => {
  const { t } = useTranslation(["workspaces"]);
  const { workspaceId } = useParams();
  const [status, setStatus] = useState<RejoinStatus>("confirm");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { user } = useUserStore();
  const { refetch: refetchSettings } = useWorkspaceSettingsStore();
  const { refetch: refetchWorkspaceInfo } = useWorkspaceInformationStore();

  const isStudent = user?.role === "student";

  const getErrorMessage = (errorCode: string) => {
    const key = `rejoinWorkspace.errors.${errorCode}`;

    return t(key, { defaultValue: "An unexpected error occurred. Please try again." });
  };

  const rejoinWorkspace = async () => {
    try {
      setStatus("rejoining");
      const endpoint = isStudent
        ? WORKSPACES_API_ENDPOINTS(workspaceId ?? "").STUDENT.REJOIN
        : WORKSPACES_API_ENDPOINTS(workspaceId ?? "").TUTOR.REJOIN;

      await axiosApiInstance.post(endpoint);

      setStatus("success");
      toast.success(t("rejoinWorkspace.toastSuccess.title", { defaultValue: "Success!" }), {
        description: t("rejoinWorkspace.toastSuccess.description", {
          defaultValue: "You have successfully rejoined the workspace.",
        }),
      });

      // Refetch workspace data
      await refetchWorkspaceInfo(workspaceId!);
      await refetchSettings(workspaceId!);

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: any) {
      setStatus("error");

      const errorCode = error?.response?.data?.code;
      const errorMsg = errorCode
        ? getErrorMessage(errorCode)
        : t("rejoinWorkspace.toastError.description", {
            defaultValue: "An unexpected error occurred. Please try again.",
          });

      setErrorMessage(errorMsg);

      toast.error(
        t("rejoinWorkspace.toastError.title", { defaultValue: "Error rejoining workspace" }),
        {
          description: errorMsg,
        },
      );
    }
  };

  const debouncedRejoinWorkspace = useDebouncedCallback(() => rejoinWorkspace(), 500);

  const handleConfirm = () => {
    debouncedRejoinWorkspace();
  };

  const handleCancel = () => {
    setStatus("confirm");
    setErrorMessage("");
    onClose();
  };

  const getIconAndColor = () => {
    switch (status) {
      case "confirm":
        return {
          icon: <AlertCircle className="h-12 w-12 text-blue-600" />,
          gradientFrom: "from-blue-600",
          gradientTo: "to-blue-800",
        };
      case "rejoining":
        return {
          icon: <Loader2 className="text-accent-foreground h-12 w-12 animate-spin" />,
          gradientFrom: "from-blue-600",
          gradientTo: "to-blue-800",
        };
      case "success":
        return {
          icon: <Check className="h-12 w-12 text-green-600" />,
          gradientFrom: "from-green-600",
          gradientTo: "to-green-800",
        };
      case "error":
        return {
          icon: <X className="h-12 w-12 text-red-600" />,
          gradientFrom: "from-red-600",
          gradientTo: "to-red-800",
        };
    }
  };

  const { icon, gradientFrom, gradientTo } = getIconAndColor();

  if (!workspaceId) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-foreground/80 text-accent-foreground w-fit max-w-4xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl max-sm:h-dvh max-sm:w-screen max-sm:rounded-none [&_>_button]:z-50!">
        <motion.div
          className="relative h-full"
          initial={false}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="absolute inset-0 opacity-50">
            <div
              className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${gradientFrom}/40 ${gradientTo}/60 max-sm:-top-6 max-sm:-right-6 max-sm:h-20 max-sm:w-20`}
            />
            <div
              className={`absolute -bottom-8 -left-8 h-24 w-24 rounded-full bg-gradient-to-tr ${gradientFrom}/30 ${gradientTo.replace("800", "600")}/50 max-sm:-bottom-4 max-sm:-left-4 max-sm:h-16 max-sm:w-16`}
            />
          </div>

          <ScrollArea className="h-[min(60dvh,500px)] min-w-[min(100dvw,800px)] max-sm:h-dvh">
            <div className="relative z-10 h-full min-h-[350px] max-sm:min-h-[calc(100dvh-2rem)]">
              <AnimatePresence initial={false} mode="wait">
                {status === "success" || status === "error" || status === "rejoining" ? (
                  <motion.div
                    key="status"
                    animate={{ opacity: 1 }}
                    className="flex h-full w-full items-center justify-center p-6 max-sm:p-4"
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    <div className="w-full max-w-lg text-center">
                      <motion.div
                        animate={{ scale: 1 }}
                        className="mb-6 flex justify-center"
                        initial={{ scale: 0 }}
                        transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
                      >
                        <div
                          className={`flex h-20 w-20 items-center justify-center rounded-full border-2 backdrop-blur-sm ${
                            status === "rejoining"
                              ? "border-blue-500/30 bg-blue-500/20"
                              : status === "success"
                                ? "border-green-500/30 bg-green-500/20"
                                : "border-red-500/30 bg-red-500/20"
                          }`}
                        >
                          {icon}
                        </div>
                      </motion.div>

                      <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        initial={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                      >
                        <h2 className="text-accent-foreground mb-3 text-2xl font-bold">
                          {status === "rejoining" &&
                            t("rejoinWorkspace.title", { defaultValue: "Rejoining workspace..." })}
                          {status === "success" &&
                            t("rejoinWorkspace.successTitle", {
                              defaultValue: "Successfully rejoined!",
                            })}
                          {status === "error" &&
                            t("rejoinWorkspace.errorTitle", { defaultValue: "Unable to rejoin" })}
                        </h2>
                        <p className="text-muted-foreground text-base leading-relaxed">
                          {status === "rejoining" &&
                            t("rejoinWorkspace.pleaseWait", {
                              defaultValue: "Please wait while we process your request.",
                            })}
                          {status === "success" &&
                            t("rejoinWorkspace.successDescription", {
                              defaultValue: "Welcome back to the workspace!",
                            })}
                          {status === "error" && errorMessage}
                        </p>
                      </motion.div>

                      {status !== "error" && (
                        <motion.div
                          animate={{ opacity: 1 }}
                          className="bg-muted mt-8 h-1.5 w-full overflow-hidden rounded-full"
                          initial={{ opacity: 0 }}
                          transition={{ duration: 0.2, delay: 0.2 }}
                        >
                          {status === "rejoining" && (
                            <motion.div
                              animate={{ width: "100%" }}
                              className="bg-primary h-full"
                              initial={{ width: "0%" }}
                              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                            />
                          )}
                          {status === "success" && (
                            <motion.div
                              animate={{ width: "100%" }}
                              className="h-full bg-green-500"
                              initial={{ width: "0%" }}
                              transition={{ duration: 0.3 }}
                            />
                          )}
                        </motion.div>
                      )}

                      {status === "error" && (
                        <motion.div
                          animate={{ opacity: 1 }}
                          className="mt-6"
                          initial={{ opacity: 0 }}
                          transition={{ duration: 0.25, delay: 0.3 }}
                        >
                          <Button
                            className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground min-w-32 rounded-xl"
                            variant="ghost"
                            onClick={handleCancel}
                          >
                            {t("rejoinWorkspace.cancelButton", { defaultValue: "Cancel" })}
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="confirm"
                    animate={{ opacity: 1 }}
                    className="flex h-full w-full items-center justify-center p-8 max-sm:p-4"
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    <div className="w-full max-w-2xl text-center">
                      {/* Header with Icon */}
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        className="mb-8 max-sm:mb-6"
                        initial={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
                      >
                        <div className="mb-6 flex items-center justify-center gap-4">
                          <motion.div
                            animate={{ scale: 1 }}
                            className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-blue-500/30 bg-blue-500/20 backdrop-blur-sm"
                            initial={{ scale: 0 }}
                            transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
                          >
                            <UserPlus className="h-8 w-8 text-blue-600" />
                          </motion.div>
                          <h2 className="text-accent-foreground text-3xl font-bold max-sm:text-2xl">
                            {t("rejoinWorkspace.confirmTitle", { defaultValue: "Rejoin Class?" })}
                          </h2>
                        </div>
                        <p className="text-accent-foreground/70 text-lg leading-relaxed max-sm:text-base">
                          {t("rejoinWorkspace.confirmDescription", {
                            defaultValue:
                              "This will bring back your access to all resources and activities in this class.",
                          })}
                        </p>
                      </motion.div>

                      {/* Enhanced Description */}
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="mb-8 max-sm:mb-6"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.25, delay: 0.25, ease: "easeOut" }}
                      >
                        <div className="rounded-xl bg-gradient-to-br from-blue-500/5 to-green-600/5 p-6 max-sm:p-4">
                          <h3 className="text-accent-foreground mb-4 text-lg font-semibold">
                            {t("workspaces:rejoinWorkspace.whatWillHappen", {
                              defaultValue: "What you'll regain access to:",
                            })}
                          </h3>
                          <ul className="text-accent-foreground/70 space-y-3 text-base max-sm:space-y-2 max-sm:text-sm">
                            <li className="flex items-start gap-3">
                              <Icon
                                className="mt-1 flex-shrink-0 text-green-500"
                                height="1rem"
                                icon="tabler:check"
                                width="1rem"
                              />
                              <span className="leading-relaxed">
                                {t("workspaces:rejoinWorkspace.benefit1", {
                                  defaultValue: "All workspace resources and materials",
                                })}
                              </span>
                            </li>
                            <li className="flex items-start gap-3">
                              <Icon
                                className="mt-1 flex-shrink-0 text-green-500"
                                height="1rem"
                                icon="tabler:check"
                                width="1rem"
                              />
                              <span className="leading-relaxed">
                                {t("workspaces:rejoinWorkspace.benefit2", {
                                  defaultValue: "Ability to book and attend lessons",
                                })}
                              </span>
                            </li>
                            <li className="flex items-start gap-3">
                              <Icon
                                className="mt-1 flex-shrink-0 text-green-500"
                                height="1rem"
                                icon="tabler:check"
                                width="1rem"
                              />
                              <span className="leading-relaxed">
                                {t("workspaces:rejoinWorkspace.benefit3", {
                                  defaultValue: "Participation in classroom activities",
                                })}
                              </span>
                            </li>
                            <li className="flex items-start gap-3">
                              <Icon
                                className="mt-1 flex-shrink-0 text-green-500"
                                height="1rem"
                                icon="tabler:check"
                                width="1rem"
                              />
                              <span className="leading-relaxed">
                                {t("workspaces:rejoinWorkspace.benefit4", {
                                  defaultValue: "Communication with tutors and classmates",
                                })}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </motion.div>

                      {/* Action Buttons */}
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="flex gap-4 max-sm:flex-col"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.25, delay: 0.3, ease: "easeOut" }}
                      >
                        <Button
                          className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground flex-1 rounded-xl"
                          variant="ghost"
                          onClick={handleCancel}
                        >
                          {t("rejoinWorkspace.cancelButton", { defaultValue: "Cancel" })}
                        </Button>
                        <Button
                          className="flex-1 gap-2 rounded-xl"
                          variant="foreground"
                          onClick={handleConfirm}
                        >
                          <UserPlus className="h-4 w-4" />
                          {t("rejoinWorkspace.confirmButton", { defaultValue: "Rejoin" })}
                        </Button>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ScrollArea>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
